/**
 * ChartSyncGroup — linked zoom/pan across multiple lightweight-charts instances.
 *
 * Design:
 * - Module-level registry (no React re-renders on every pan frame).
 * - Any chart can drive; peers receive the same visible window.
 * - Prefer time-range sync so X-axes stay aligned even when bar counts differ
 *   slightly across symbols (e.g. NIFTY vs FINNIFTY on live ticks).
 * - Fall back to logical range when time range is unavailable.
 * - Re-entry guard + double-rAF release prevents feedback loops and lag.
 */

/** @type {Map<string, ReturnType<typeof createGroup>>} */
const groups = new Map();

function rangesEqual(a, b) {
  if (!a || !b) return false;
  return a.from === b.from && a.to === b.to;
}

function timeRangesEqual(a, b) {
  if (!a || !b) return false;
  // Times may be numbers (unix) or business-day strings
  return String(a.from) === String(b.from) && String(a.to) === String(b.to);
}

function doubleRaf(fn) {
  requestAnimationFrame(() => {
    requestAnimationFrame(fn);
  });
}

function createGroup(groupId) {
  /** @type {Map<string, { chart: import('lightweight-charts').IChartApi }>} */
  const members = new Map();
  /** True while we are applying a range to peers — ignore nested events. */
  let applying = false;
  /** Last broadcast snapshot (avoids redundant work on rapid identical events). */
  let lastBroadcast = null;

  function releaseApplying() {
    // LWC may emit visible-range callbacks sync or on the next frame.
    doubleRaf(() => {
      applying = false;
    });
  }

  /**
   * Push the driver's visible window to every other chart in the group.
   * @param {string} sourceId
   * @param {{ from: number, to: number } | null} logicalRange
   */
  function broadcast(sourceId, logicalRange) {
    if (applying) return;
    const source = members.get(sourceId);
    if (!source?.chart) return;

    let timeRange = null;
    try {
      timeRange = source.chart.timeScale().getVisibleRange();
    } catch (_) {
      timeRange = null;
    }

    const hasTime =
      timeRange
      && timeRange.from != null
      && timeRange.to != null;
    const hasLogical =
      logicalRange
      && Number.isFinite(logicalRange.from)
      && Number.isFinite(logicalRange.to);

    if (!hasTime && !hasLogical) return;

    // Deduplicate identical broadcasts (rapid wheel / drag noise)
    const snap = hasTime
      ? { k: 't', from: timeRange.from, to: timeRange.to }
      : { k: 'l', from: logicalRange.from, to: logicalRange.to };
    if (
      lastBroadcast
      && lastBroadcast.k === snap.k
      && String(lastBroadcast.from) === String(snap.from)
      && String(lastBroadcast.to) === String(snap.to)
    ) {
      return;
    }
    lastBroadcast = snap;

    applying = true;
    try {
      for (const [id, m] of members) {
        if (id === sourceId || !m?.chart) continue;
        try {
          const ts = m.chart.timeScale();
          if (hasTime) {
            const cur = ts.getVisibleRange();
            if (!timeRangesEqual(cur, timeRange)) {
              ts.setVisibleRange(timeRange);
            }
          } else if (hasLogical) {
            const cur = ts.getVisibleLogicalRange();
            if (!rangesEqual(cur, logicalRange)) {
              ts.setVisibleLogicalRange(logicalRange);
            }
          }
        } catch (_) {
          // Peer may not yet have data covering the range (load race / empty).
        }
      }
    } finally {
      releaseApplying();
    }
  }

  return {
    groupId,

    get size() {
      return members.size;
    },

    get isApplying() {
      return applying;
    },

    /**
     * Register a chart. Safe to call again for the same id (replaces).
     * On join, adopts an existing peer's visible window when available.
     */
    register(id, chart) {
      if (!id || !chart) return;
      if (members.has(id)) this.unregister(id);

      members.set(id, { chart });

      // Adopt current window from any peer that already has a range set
      if (members.size > 1 && !applying) {
        for (const [otherId, m] of members) {
          if (otherId === id) continue;
          try {
            const timeRange = m.chart.timeScale().getVisibleRange();
            const logicalRange = m.chart.timeScale().getVisibleLogicalRange();
            if (
              (timeRange && timeRange.from != null && timeRange.to != null)
              || (logicalRange && Number.isFinite(logicalRange.from))
            ) {
              applying = true;
              try {
                if (timeRange?.from != null && timeRange?.to != null) {
                  chart.timeScale().setVisibleRange(timeRange);
                } else if (logicalRange) {
                  chart.timeScale().setVisibleLogicalRange(logicalRange);
                }
              } catch (_) {
                /* ignore — new chart may have shorter series */
              } finally {
                releaseApplying();
              }
              break;
            }
          } catch (_) {
            /* peer not ready */
          }
        }
      }
    },

    unregister(id) {
      members.delete(id);
      if (members.size === 0) {
        lastBroadcast = null;
        groups.delete(groupId);
      }
    },

    /**
     * Driver entry point — call from subscribeVisibleLogicalRangeChange.
     * @param {string} sourceId
     * @param {{ from: number, to: number } | null} logicalRange
     */
    onLogicalRangeChange(sourceId, logicalRange) {
      if (applying || !logicalRange) return;
      broadcast(sourceId, logicalRange);
    },

    /**
     * Imperatively set the same window on every member (e.g. after TF switch).
     * @param {{ from: unknown, to: unknown }} timeRange
     * @param {string | null} exceptId
     */
    setTimeRange(timeRange, exceptId = null) {
      if (!timeRange || timeRange.from == null || timeRange.to == null) return;
      applying = true;
      lastBroadcast = { k: 't', from: timeRange.from, to: timeRange.to };
      try {
        for (const [id, m] of members) {
          if (id === exceptId) continue;
          try {
            m.chart.timeScale().setVisibleRange(timeRange);
          } catch (_) {}
        }
      } finally {
        releaseApplying();
      }
    },

    /** @param {{ from: number, to: number }} logicalRange */
    setLogicalRange(logicalRange, exceptId = null) {
      if (!logicalRange) return;
      applying = true;
      lastBroadcast = { k: 'l', from: logicalRange.from, to: logicalRange.to };
      try {
        for (const [id, m] of members) {
          if (id === exceptId) continue;
          try {
            m.chart.timeScale().setVisibleLogicalRange(logicalRange);
          } catch (_) {}
        }
      } finally {
        releaseApplying();
      }
    },
  };
}

/**
 * Get or create a named sync group.
 * @param {string} groupId e.g. "idx-board-NSE"
 */
export function getChartSyncGroup(groupId) {
  if (!groupId) {
    // Soft no-op group — never throw into React render/effect paths
    return {
      groupId: '',
      size: 0,
      isApplying: false,
      register() {},
      unregister() {},
      onLogicalRangeChange() {},
      setTimeRange() {},
      setLogicalRange() {},
    };
  }
  let g = groups.get(groupId);
  if (!g) {
    g = createGroup(groupId);
    groups.set(groupId, g);
  }
  return g;
}

/** Drop a group (optional; empty groups auto-delete on last unregister). */
export function disposeChartSyncGroup(groupId) {
  groups.delete(groupId);
}
